import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import usePageParams from 'hooks/usePageParams';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { sentryError } from 'utils';
import { Loader } from 'ncoded-component-library';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import { SIZE } from 'models/Size';
import { DataRoomShareableLinkReport } from 'models/ShareableLink';
import ResolvePagination from 'components/ResolvePagination';
import ViewerReportLog from './components/ViewerReportLog';
import useMatchMedia from 'hooks/useMatchMedia';

import './ShareableLinkViewers.styles.scss';
import './ShareableLinkViewers.styles.responsive.scss';

const LIMIT = '20';
const PAGE_REPORT = 'pageReport';

type ShareableLinkViewersProps = {
  className?: string;
  shareableLinkId: string;
};

const ShareableLinkViewers: React.FC<ShareableLinkViewersProps> = (props) => {
  const { className, shareableLinkId } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [reports, setReports] = useState<DataRoomShareableLinkReport[]>([]);

  const pageReport = usePageParams(PAGE_REPORT);

  const isEmpty = useMemo(() => !reports?.length, [reports?.length]);

  const classes = classNames(
    'shareable-link-viewers',
    { loading: isLoading && isEmpty },
    className,
  );

  const isPhablet = useMatchMedia('isPhablet');

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { items, totalPages },
        } = await dataRoomApi.shareableLinks.getShareableLinkReports(
          shareableLinkId,
          {
            $page: pageReport,
            $limit: LIMIT,
            $relations: '["dataRoomShareableLinkConsumer","dataRoomNode"]',
          },
        );

        setReports(items);
        setTotalPages(totalPages);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [shareableLinkId, pageReport]);

  return (
    <div className={classes}>
      {isLoading && isEmpty ? (
        <Loader />
      ) : isEmpty ? (
        <ReportsEmptyState size={isPhablet ? SIZE.SMALL : SIZE.BIG} />
      ) : (
        <>
          <div className="shareable-link-viewers__logs">
            {reports.map((report) => (
              <ViewerReportLog key={report._id} report={report} />
            ))}
          </div>
          <ResolvePagination totalPages={totalPages} pageParam={PAGE_REPORT} />
        </>
      )}

      {isLoading && !isEmpty && <Loader />}
    </div>
  );
};

export default ShareableLinkViewers;
